<template>
    <b-card title="Promotion">
  
        <!-- search input -->
        <div class="custom-search d-flex justify-content-between">
            <b-form-group class="justify-content-end">
                <div class="d-flex align-items-center">
                    <label class="mr-1">Search</label> 
                    <b-form-input
                        v-model="searchTerm"
                        placeholder="Search"
                        type="text"
                        class="d-inline-block"
                    />
                </div>
            </b-form-group>
            <b-form-group v-if="this.showAdd">
                <div class="d-flex align-items-center">
                    <b-button
                        id="toggle-btn"
                        v-b-modal.modal-prevent-closing
                        v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                        variant="outline-primary"
                        @click="addNew()"
                    >
                        <feather-icon
                            icon="PlusIcon"
                            class="mr-50"
                        />
                        <span class="align-middle">Add New</span>
                    </b-button>
                </div>
            </b-form-group>
        </div>
    
        <!-- table -->
        <vue-good-table
            :columns="columns"
            :rows="rows"
            :rtl="direction"
            :search-options="{
                enabled: true,
                externalQuery: searchTerm 
            }"
            :pagination-options="{
                enabled: true,
                perPage:pageLength
            }"
            styleClass="vgt-table striped condensed no-outline"
        >
    
            <template
                slot="table-row"
                slot-scope="props"
            >

                <!-- Column: Image -->
                <span
                    v-if="props.column.field === 'banner'"
                    class="text-nowrap" 
                >
                    <img style="height: 80px;" :src="host + props.row.image" alt="">
                </span>
        
                <!-- Column: Index -->
                <span v-if="props.column.field === 'date'">
                    <span style="display: flex; flex-direction: column; gap: 12px;">
                        <span>From: {{ props.row.start_date }}</span>
                        <span>To: {{ props.row.end_date }}</span>
                    </span>
                </span>

                <!-- Column: Index -->
                <span v-else-if="props.column.field === 'index'">
                    <span>{{props.row.originalIndex + 1}}</span>
                </span>

                <!-- Column: Discount -->
                <span v-else-if="props.column.field === 'discount'">
                    <span style="display: flex; flex-direction: column; gap: 12px;">
                        <span>Premium Profile: {{ props.row.premium_profile_discount }}%</span>
                        <span>Match Payment: {{ props.row.match_payment_discount }}%</span>
                    </span>
                </span>

                <!-- Column: Status -->
                <span v-else-if="props.column.field === 'is_active'">
                    <span>
                        <span v-if="props.row.is_active == true" @click="updateStatus(props.row.id)" style="cursor:pointer"><b-badge pill variant="light-success">Active</b-badge></span>
                        <span v-else @click="updateStatus(props.row.id)" style="cursor:pointer"><b-badge pill variant="light-danger">Inactive</b-badge></span>
                    </span>
                </span>

                <!-- Column: Action -->
                <span v-else-if="props.column.field === 'action'" >
                    <span>
                        <b-button @click="edit(props.row)"
                            v-b-modal.modal-prevent-closing
                            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                            variant="primary" class="btn-icon rounded-circle mr-1"
                            v-if="showEdit"
                        >
                            <feather-icon
                                icon="Edit2Icon"
                            />
                        </b-button>
                        <b-button @click="deleteType(props.row)"
                            id="delete-btn"
                            v-b-modal.modal-delete
                            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                            variant="primary" class="btn-icon rounded-circle"
                            v-if="showDelete"
                        >
                            <feather-icon
                                icon="TrashIcon"
                            />
                        </b-button>
                    </span>
                </span>
        
                <!-- Column: Common -->
                <span v-else>
                    {{ props.formattedRow[props.column.field] }}
                </span>
            </template>
    
            <!-- pagination -->
            <template
                slot="pagination-bottom"
                slot-scope="props"
            >
                <div class="d-flex justify-content-between flex-wrap">
                    <div class="d-flex align-items-center mb-0 mt-1">
                        <span class="text-nowrap ">
                            Showing 1 to
                        </span>
                        <b-form-select
                            v-model="pageLength"
                            :options="['3','5','10','50','100']"
                            class="mx-1"
                            @input="(value)=>props.perPageChanged({currentPerPage:value})"
                        />
                        <span class="text-nowrap"> of {{ props.total }} entries </span>
                    </div>
                    <div>
                        <b-pagination
                            :value="1"
                            :total-rows="props.total"
                            :per-page="pageLength"
                            first-number
                            last-number
                            align="right"
                            prev-class="prev-item"
                            next-class="next-item"
                            class="mt-1 mb-0"
                            @input="(value)=>props.pageChanged({currentPage:value})"
                        >
                            <template #prev-text>
                                <feather-icon
                                    icon="ChevronLeftIcon"
                                    size="18"
                                />
                            </template>
                            <template #next-text>
                                <feather-icon
                                    icon="ChevronRightIcon"
                                    size="18"
                                />
                            </template>
                        </b-pagination>
                    </div>
                </div>
            </template>
        </vue-good-table>
    
        <!-- Promotion Create / Edit -->
        <b-modal
            id="modal-prevent-closing"
            ref="my-modal"
            centered
            :title="getModalTitle()"
            ok-title="Submit"
            cancel-variant="outline-secondary"
            @show="resetModal"
            @hidden="resetModal"
            @ok="handleOk"
        >
            <form
                ref="form"
                @submit.stop.prevent="handleSubmit"
            >
                <b-form-group
                    :state="nameState"
                    label="Title"
                    label-for="name-input"
                >
                    <b-form-input
                        id="name-input"
                        type="text"
                        placeholder="Enter promotion title"
                        v-model="modify.title"
                        :state="nameState"
                    />
                </b-form-group>

                <b-form-group
                    :state="nameState"
                    label="Start Date"
                    label-for="name-input"
                >
                    <b-form-datepicker
                        id="example-datepicker"
                        v-model="modify.start_date"
                    />
                </b-form-group>

                <b-form-group
                    :state="nameState"
                    label="End Date"
                    label-for="name-input"
                >
                    <b-form-datepicker
                        id="example-datepicker-2"
                        v-model="modify.end_date"
                    />
                </b-form-group>

                <b-form-group
                    :state="nameState"
                    label="Premium Profile Discount in % (Number field)"
                    label-for="name-input"
                >
                    <b-form-input
                        id="name-input"
                        type="number"
                        placeholder="Enter number of percentage"
                        v-model="modify.premium_profile_discount"
                        :state="nameState"
                    />
                </b-form-group>

                <b-form-group
                    :state="nameState"
                    label="Match Payment Discount in % (Number field)"
                    label-for="name-input"
                >
                    <b-form-input
                        id="name-input"
                        type="number"
                        placeholder="Enter number of percentage"
                        v-model="modify.match_payment_discount"
                        :state="nameState"
                    />
                </b-form-group>

                <b-form-group
                    label="Upload Promotion Banner"
                >
                    <b-form-file id="file-default" v-model="modify.image" placeholder="Choose a image or drop it here..." drop-placeholder="Drop image here..." @change="uploadAttachment"></b-form-file>
                </b-form-group>

                <div style="display: flex; flex-direction: column; margin-top: 30px;">
                    <h7>Uploaded Promotion Banner:</h7>
                    <img style="max-width: 300px; margin-top: 8px;" :src="selected_image" alt="">
                </div>
            </form>
        </b-modal>

        <!-- Promotion Delete -->
        <b-modal
            id="modal-delete"
            ref="modal-delete"
            centered
            modal-class="modal-danger"
            title="Are you sure?"
        >
            <b-card-text class="border-2">
                <span>If you delete, you won't be able to revert this action!!</span>
            </b-card-text>
    
            <template #modal-footer>
                <div class="w-100">
                    <b-button
                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                        variant="danger"
                        class="float-right"
                        @click="confirmDelete()"
                    >
                        Accept
                    </b-button>
                    <b-button
                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                        variant="primary"
                        class="float-right mr-1"
                        @click="$refs['modal-delete'].toggle('#delete-btn')"
                    >
                        Cancel
                    </b-button>
                </div>
            </template>
        </b-modal>
    </b-card>
</template>
  
<script>
import {
    BAvatar,
    BButton,
    BBadge,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BDropdown,
    BDropdownItem,
    BFormFile, 
    BCard,
    BModal,
    VBModal,
    BCardText,
    BFormDatepicker
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import store from '@/store/index'
import vSelect from "vue-select"
import Ripple from "vue-ripple-directive";
import {mapState} from "vuex";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import moment from 'moment'; 

export default {
    components: {
        VueGoodTable,
        BAvatar,
        BCard,
        BBadge,
        BButton,
        BPagination,
        BFormGroup,
        BFormInput,
        BFormSelect,
        BDropdown,
        BFormFile,
        BDropdownItem,
        BModal,
        BCardText,
        vSelect,
        BFormDatepicker
    },

    directives: {
        'b-modal': VBModal,
        Ripple,
    },

    computed: {
        ...mapState('promotion', ["promotion","promotionError","promotionLoading", "submitError","submitSuccess","submitLoading"]),
    
        direction() {
            if (store.state.appConfig.isRTL) {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.dir = true
                return this.dir
            }
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.dir = false
            return this.dir
        }
    },

    watch: {
        promotion(newValue, oldValue) {
            if (newValue) {
                this.rows = this.promotion
            }
        }
    },

    data() {
        return {
            host: 'https://api-biyeruti.smicee.com',
            selected_image: null,
            nameState: null,
            addNewMode:false,
            popupActive:true,
            modify:{
                id: null,
                title: null,
                start_date: null,
                end_date: null,
                image: null,
                premium_profile_discount: null,
                match_payment_discount: null
            },
            pageLength: 10,
            dir: false,
            columns: [
                {
                    label: '#',
                    field: 'index',
                    type:'number',
                    tdClass: 'text-center',
                    thClass:'text-center'
                },
                {
                    label: 'Title',
                    field: 'title',
                    tdClass: 'text-center',
                    thClass:'text-center'
                },
                {
                    label: 'Banner',
                    field: 'banner',
                    tdClass: 'text-center',
                    thClass:'text-center'
                },
                {
                    label: 'Date Range',
                    field: 'date',
                    tdClass: 'text-center',
                    thClass:'text-center'
                },
                {
                    label: 'Discount',
                    field: 'discount',
                    tdClass: 'text-center',
                    thClass:'text-center'
                },
                {
                    label: 'Status',
                    field: 'is_active',
                    tdClass: 'text-center',
                    thClass:'text-center'
                }
            ],
            showAdd:true,
            showEdit:true,
            showDelete:true,
            rows: [],
            searchTerm: '',
        } 
    },

    created() {
        this.$store.dispatch('promotion/fetchPromotion')
        this.rows = this.promotion
        let permissions=JSON.parse(localStorage.getItem('permissions'))
        // this.showAdd=permissions.includes("add_state")
        // this.showEdit=permissions.includes("change_state")
        // this.showDelete=permissions.includes("delete_state")
        if(this.showEdit||this.showDelete){
            this.columns.push( {
            label: 'Action',
            field: 'action',
            tdClass: 'text-center',
            thClass:'text-center',
            sortable: false,
            })
        }
    },

    methods:{
        uploadAttachment(e) {
            const image = e.target.files[0];
            const reader = new FileReader();
            reader.readAsDataURL(image);
            reader.onload = e =>{
                this.modify.image = e.target.result;
                this.selected_image = e.target.result;
            };
        },

        checkFormValidity() {
            const valid = this.$refs.form.checkValidity()
            this.nameState = valid
            return valid
        },

        resetModal() {
            this.nameState = null
        },

        handleOk(bvModalEvt) {
            bvModalEvt.preventDefault()
            this.handleSubmit()
        },

        addNew () {
            this.modify.name=undefined
            this.addNewMode = true;
        },

        edit (data) {
            this.addNewMode = false;
            this.modify.id = data.id
            this.modify.title = data.title;
            this.modify.start_date = data.start_date;
            this.modify.end_date = data.end_date;
            this.modify.premium_profile_discount = data.premium_profile_discount;
            this.modify.match_payment_discount = data.match_payment_discount;
            this.selected_image = this.host + data.image;
            // this.popupActive=true;
        },

        async handleSubmit() {
            this.modify.start_date =  moment(this.modify.start_date).startOf("day").add(1, "second").format("YYYY-MM-DDTHH:mm:ss");
            this.modify.end_date =  moment(this.modify.end_date).endOf("day").format("YYYY-MM-DDTHH:mm:ss");

            if (!this.checkFormValidity()) {
                return
            }
            if(this.addNewMode){
                await this.$store.dispatch('promotion/addPromotion', this.modify).then(result=>{
                    if(result.code == 200) {
                        this.$toast({
                            component: ToastificationContent,
                            position: 'top-right',
                            props: {
                                title: `Success`,
                                icon: 'ThumbsUpIcon',
                                variant: 'success',
                                text: result.response
                            }
                        })
                    } else {
                        this.$toast({
                            component: ToastificationContent,
                            position: 'top-right',
                            props: {
                                title: `Error`,
                                icon: 'ThumbsDownIcon',
                                variant: 'error',
                                text: result.response
                            }
                        })
                    }
                })
            }else{
                await this.$store.dispatch('promotion/updatePromotion', this.modify).then(result=> {
                    if(result.code == 200) {
                        this.$toast({
                            component: ToastificationContent,
                            position: 'top-right',
                            props: {
                                title: `Success`,
                                icon: 'ThumbsUpIcon',
                                variant: 'success',
                                text: result.response
                            }
                        })
                    } else {
                        this.$toast({
                            component: ToastificationContent,
                            position: 'top-right',
                            props: {
                                title: `Error`,
                                icon: 'ThumbsDownIcon',
                                variant: 'error',
                                text: result.response
                            }
                        })
                    }
                })
            }
            this.modify.id = null
            this.modify.title = null
            this.modify.start_date = null
            this.modify.end_date = null
            this.modify.premium_profile_discount = null
            this.modify.match_payment_discount = null
            this.modify.image = null
            this.selected_image = null

            this.$nextTick(() => {
                this.$refs['my-modal'].toggle('#toggle-btn')
            })
        },

        initValues(){
            this.modify={
                id: null,
                name: null
            }
        },

        updateStatus(id) {
            this.$store.dispatch("promotion/updatePromotionStatus", id).then(result=>{
                if(result.code == 200) {
                    this.$toast({
                        component: ToastificationContent,
                        position: 'top-right',
                        props: {
                            title: `Success`,
                            icon: 'ThumbsUpIcon',
                            variant: 'success',
                            text: 'Status updated successfully.',
                        },
                    })
                } else {
                    this.$toast({
                        component: ToastificationContent,
                        position: 'top-right',
                        props: {
                            title: `Error`,
                            icon: 'ThumbsDownIcon',
                            variant: 'error',
                            text: result.response
                        }
                    })
                }
            })
        },

        deleteType(data){
            this.modify.id = data.id
        },

        async confirmDelete(){
            await this.$store.dispatch("promotion/deletePromotion", this.modify.id).then(result=>{
                if(result.code == 200) {
                    this.$toast({
                        component: ToastificationContent,
                        position: 'top-right',
                        props: {
                            title: `Success`,
                            icon: 'ThumbsUpIcon',
                            variant: 'success',
                            text: result.message
                        }
                    })
                } else { 
                    this.$toast({
                        component: ToastificationContent,
                        position: 'top-right',
                        props: {
                            title: `Error`,
                            icon: 'ThumbsDownIcon',
                            variant: 'error',
                            text: result.message
                        }
                    })
                }
            })
            this.$nextTick(() => {
                this.$refs['modal-delete'].toggle('#delete-btn')
            })
        },

        getModalTitle(){
            return (this.addNewMode? 'Add New ' : 'Update ') +'Promotion'
        }
    }
}
</script>

<style lang="scss" >
@import '@core/scss/vue/libs/vue-select.scss';

@import '@core/scss/vue/libs/vue-good-table.scss';
</style>